import { useEffect, useMemo, useRef, useState } from "react";

import { loadAmap, type AMapNativeMap, type AMapNativeMarker } from "../../hotel-map";
import type { StayResourceGeoCandidate, StayResourceSpuItem } from "../types";

type MapPoint = {
  id: string;
  lng: number;
  lat: number;
  label: string;
  final: boolean;
  selected: boolean;
};

type StayResourceGeoCandidateMapProps = {
  detail: StayResourceSpuItem;
  candidates: StayResourceGeoCandidate[];
  selectedCandidateId?: string;
};

/** 在房资源 GEO 详情中用高德底图展示最终坐标与核验候选。 */
export function StayResourceGeoCandidateMap({
  detail,
  candidates,
  selectedCandidateId = "",
}: StayResourceGeoCandidateMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<AMapNativeMap | null>(null);
  const markersRef = useRef<AMapNativeMarker[]>([]);
  const [error, setError] = useState("");

  const points = useMemo<MapPoint[]>(() => [
    ...(detail.lng !== undefined && detail.lat !== undefined
      ? [{
        id: "final",
        lng: detail.lng,
        lat: detail.lat,
        label: "最终位置",
        final: true,
        selected: false,
      }]
      : []),
    ...candidates.map((candidate) => ({
      id: candidate.candidateId,
      lng: candidate.lng,
      lat: candidate.lat,
      label: candidate.name,
      final: false,
      selected: candidate.candidateId === selectedCandidateId,
    })),
  ], [candidates, detail.lat, detail.lng, selectedCandidateId]);

  const focusPoint = useMemo(() => {
    const selected = points.find((point) => point.selected);
    if (selected) return selected;
    const finalPoint = points.find((point) => point.final);
    if (finalPoint) return finalPoint;
    return points[0];
  }, [points]);

  const pointsKey = points.map((point) => `${point.id}:${point.lng}:${point.lat}:${point.selected ? 1 : 0}`).join("|");
  const focusKey = focusPoint ? `${focusPoint.id}:${focusPoint.lng}:${focusPoint.lat}` : "";

  useEffect(() => {
    let cancelled = false;
    if (!points.length || !focusPoint) {
      return undefined;
    }

    /** 加载高德底图并同步最终坐标与候选点标记。 */
    async function mountMap() {
      const container = containerRef.current;
      if (!container || !focusPoint) return;
      try {
        const amap = await loadAmap({
          key: import.meta.env.VITE_AMAP_JS_API_KEY ?? "",
          securityCode: import.meta.env.VITE_AMAP_SECURITY_JS_CODE,
        });
        if (cancelled || !containerRef.current) return;
        setError("");

        const center: [number, number] = [focusPoint.lng, focusPoint.lat];
        const zoom = 16;
        let map = mapRef.current;
        if (!map) {
          map = new amap.Map(containerRef.current, { center, zoom });
          mapRef.current = map;
        }

        if (markersRef.current.length) {
          map.remove(markersRef.current);
          markersRef.current = [];
        }

        markersRef.current = points.map((point) => new amap.Marker({
          position: [point.lng, point.lat],
          content: createMarkerContent(point),
          anchor: "center",
          zIndex: point.final ? 30 : point.selected ? 25 : 10,
        }));
        map.add(markersRef.current);
        map.setZoomAndCenter(zoom, center);
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "高德地图加载失败");
        }
      }
    }

    void mountMap();
    return () => {
      cancelled = true;
    };
  }, [focusKey, focusPoint, points, pointsKey]);

  useEffect(() => () => {
    const map = mapRef.current;
    if (!map) return;
    if (markersRef.current.length) {
      map.remove(markersRef.current);
      markersRef.current = [];
    }
    map.destroy();
    mapRef.current = null;
  }, []);

  if (!points.length) {
    return (
      <div className="stay-resource-coordinate-map stay-resource-coordinate-map--empty" aria-label="GEO 候选地图">
        暂无可展示坐标
      </div>
    );
  }

  return (
    <div className="stay-resource-coordinate-map stay-resource-coordinate-map--amap" aria-label="GEO 候选地图">
      <div ref={containerRef} className="stay-resource-coordinate-map__amap" />
      {error ? <div className="stay-resource-coordinate-map__error" role="alert">{error}</div> : null}
      <div className="stay-resource-coordinate-map__legend">
        <span><i className="final" />最终位置</span>
        <span><i />核验候选</span>
        <span><i className="selected" />当前选择</span>
      </div>
    </div>
  );
}

/** 构造房资源 GEO 地图中的坐标标记 DOM。 */
function createMarkerContent(point: MapPoint): HTMLElement {
  const root = document.createElement("div");
  const classes = ["stay-resource-map-pin"];
  if (point.final) classes.push("stay-resource-map-pin--final");
  if (point.selected) classes.push("stay-resource-map-pin--selected");
  root.className = classes.join(" ");
  root.title = `${point.label} ${point.lng.toFixed(6)}, ${point.lat.toFixed(6)}`;
  const label = document.createElement("span");
  label.textContent = point.final ? "终" : point.selected ? "选" : "候";
  root.append(label);
  return root;
}
